<div class="dash-shell">
  <!-- ═══════════════════════════════════════════════════════════════════════
       PAGE HEADER
       ═══════════════════════════════════════════════════════════════════════ -->
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/ai-evaluations">AI evaluations</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Evaluation</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">AI evaluation</h1>
        <p class="page-head__subtitle">
          The full transaction — what was sent to Claude, what came back, and what it cost.
          Internal only; never shown to clients.
        </p>
      </div>
      <a mat-stroked-button routerLink="/admin/ai-evaluations">
        <app-icon name="chevron-left" [size]="16" /> Back to log
      </a>
    </div>
  </header>

  @if (loading()) {
    <div class="aievd-loading">
      <span class="sg-skeleton sg-skeleton--text" style="width: 40%; height: 1.5rem"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 90%"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 75%"></span>
    </div>
  } @else if (error()) {
    <div class="sg-empty sg-empty--bordered">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this evaluation</p>
      <p class="sg-empty__desc">It may not exist, or something went wrong. Please try again.</p>
      <button mat-stroked-button (click)="retry()">
        <app-icon name="refresh" [size]="18" /> Try again
      </button>
    </div>
  } @else if (evaluation(); as e) {
    <!-- ─── Cost & usage summary ───────────────────────────────────────────── -->
    <section class="dash-section" aria-label="Cost and usage">
      <div class="dash-kpi-grid dash-kpi-grid--2">
        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="tag" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ e.cost_usd | usd }}</p>
            <p class="dash-kpi__label">Cost (USD)</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="sparkles" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ totalTokens() | number }}</p>
            <p class="dash-kpi__label">Total tokens</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Metadata ───────────────────────────────────────────────────────── -->
    <section class="dash-section" aria-label="Details">
      <div class="sg-panel aievd-panel">
        <h2 class="aievd-panel__title">Transaction</h2>
        <dl class="aievd-facts">
          <div class="aievd-fact">
            <dt>Product</dt>
            <dd>{{ e.purpose_display }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Status</dt>
            <dd>{{ e.status_display }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Recommendation</dt>
            <dd>
              @if (e.recommendation) {
                <span class="aiev-rec" [attr.data-variant]="recVariant(e.recommendation)">
                  {{ e.recommendation_display }}
                </span>
              } @else {
                <span class="dash-muted">—</span>
              }
            </dd>
          </div>
          <div class="aievd-fact">
            <dt>Confidence</dt>
            <dd>{{ e.confidence !== null ? e.confidence + '%' : '—' }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Subject</dt>
            <dd class="aievd-mono">{{ e.target_kind }} · {{ e.target_id }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Model</dt>
            <dd class="aievd-mono">{{ e.model }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Prompt</dt>
            <dd class="aievd-mono">
              {{ e.prompt_version | dash }}
              @if (e.prompt_sha256) {
                <span class="aievd-fingerprint">&middot; {{ e.prompt_sha256 }}</span>
              }
            </dd>
          </div>
          <div class="aievd-fact">
            <dt>Requested by</dt>
            <dd>{{ e.requested_by_name | dash }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>When</dt>
            <dd>{{ e.created_at | date: 'medium' }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Latency</dt>
            <dd>{{ e.latency_ms | number }} ms</dd>
          </div>
        </dl>

        <h3 class="aievd-subhead">Token breakdown</h3>
        <dl class="aievd-facts">
          <div class="aievd-fact">
            <dt>Input</dt>
            <dd>{{ e.input_tokens | number }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Output</dt>
            <dd>{{ e.output_tokens | number }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Cache read</dt>
            <dd>{{ e.cache_read_input_tokens | number }}</dd>
          </div>
          <div class="aievd-fact">
            <dt>Cache write</dt>
            <dd>{{ e.cache_creation_input_tokens | number }}</dd>
          </div>
        </dl>
      </div>
    </section>

    <!-- ─── Failure ────────────────────────────────────────────────────────── -->
    @if (e.error) {
      <section class="dash-section" aria-label="Error">
        <div class="sg-panel aievd-panel aievd-panel--error">
          <h2 class="aievd-panel__title">
            <app-icon name="alert-triangle" [size]="18" /> Error
          </h2>
          <pre class="aievd-pre">{{ e.error }}</pre>
        </div>
      </section>
    }

    <!-- ─── What was sent ──────────────────────────────────────────────────── -->
    <section class="dash-section" aria-label="Prompt sent to Claude">
      <div class="sg-panel aievd-panel">
        <h2 class="aievd-panel__title">
          <app-icon name="file-text" [size]="18" /> Sent to Claude
        </h2>

        @if (!bodiesStored()) {
          <p class="aievd-note">
            The full prompt and response weren't stored for this call (it ran without S3
            configured, or as a stub). The metadata and cost above are still accurate.
          </p>
        }

        @if (systemPrompt()) {
          <h3 class="aievd-subhead">System prompt</h3>
          <pre class="aievd-pre">{{ systemPrompt() }}</pre>
        }

        @if (promptBlocks().length) {
          <h3 class="aievd-subhead">Message</h3>
          @for (b of promptBlocks(); track $index) {
            @if (b.kind === 'text') {
              <pre class="aievd-pre">{{ b.text }}</pre>
            } @else if (b.kind === 'document' || b.kind === 'image') {
              <div class="aievd-attach">
                <app-icon name="file-text" [size]="16" />
                <span class="aievd-attach__name">{{ b.text }}</span>
                @if (b.mediaType) {
                  <span class="aievd-attach__type">{{ b.mediaType }}</span>
                }
                @if (b.redacted) {
                  <span class="aievd-attach__redacted">file bytes not stored</span>
                }
              </div>
            } @else {
              <pre class="aievd-pre">{{ b.text }}</pre>
            }
          }
        }
      </div>
    </section>

    <!-- ─── What came back ─────────────────────────────────────────────────── -->
    <section class="dash-section" aria-label="Response from Claude">
      <div class="sg-panel aievd-panel">
        <h2 class="aievd-panel__title">
          <app-icon name="sparkles" [size]="18" /> Received from Claude
        </h2>

        @if (e.summary) {
          <h3 class="aievd-subhead">Advisory summary</h3>
          <p class="aievd-summary">{{ e.summary }}</p>
        }

        @if (responseText()) {
          <h3 class="aievd-subhead">Raw response text</h3>
          <pre class="aievd-pre">{{ responseText() }}</pre>
        }

        @if (payloadJson()) {
          <h3 class="aievd-subhead">Parsed output</h3>
          <pre class="aievd-pre">{{ payloadJson() }}</pre>
        }
      </div>
    </section>

    <!-- ─── Raw JSON disclosure ────────────────────────────────────────────── -->
    @if (bodiesStored()) {
      <section class="dash-section" aria-label="Raw bodies">
        <button mat-stroked-button type="button" (click)="toggleRaw()">
          {{ showRaw() ? 'Hide' : 'Show' }} raw request/response JSON
        </button>
        @if (showRaw()) {
          <div class="sg-panel aievd-panel">
            <h3 class="aievd-subhead">Request body</h3>
            <pre class="aievd-pre aievd-pre--scroll">{{ rawRequest() }}</pre>
            <h3 class="aievd-subhead">Response body</h3>
            <pre class="aievd-pre aievd-pre--scroll">{{ rawResponse() }}</pre>
          </div>
        }
      </section>
    }
  }
</div>
